import React, { useCallback } from "react";
import { Text, View } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import { capitalize } from "./capitalize";
import { handleClick } from "lib/utils/navigator";


export const TemplateElevenProfileSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  return (
    <View
      style={{
        ...styles.flexRow,
        gap: spacing["3"], 
        marginTop: spacing["4"], // spacing[5] 
        ...style,
      }}
    >
      {heading && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"],
            width: "20vw",
            textAlign: "right",
          }}
        >
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
              color: themeColor || DEFAULT_FONT_COLOR,
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
        </View>
      )}
      <View
        style={{
          ...styles.flexCol,
          // gap: spacing["8"],
          width: "80vw",
          // borderLeft: "1px solid gray",
          // paddingLeft: spacing["4"],
        }}
      >
        {children}
      </View>
    </View>
  );
};

export const TemplateElevenResumeSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  return (
    <div
onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        gap: spacing["0.5"], // spacing[1] 
        marginTop: spacing["4"], //spacing[10] 
        cursor:"pointer"
      }}
    >
      {heading && (
        <View
          style={{
            ...styles.flexCol,
            // padding: spacing["1.5"],
            width: "100%",
            textAlign: "left",
           
          }}
        >
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt",
              color: DEFAULT_FONT_COLOR,
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {capitalize(heading)}
          </Text>
        </View>
      )}
      <View
        style={{
          ...styles.flexCol,
          gap: spacing["1"], // spacing[2] 
          paddingLeft: spacing["2"] //  spacing[3] 
        }}
      >
        {children}
      </View>
    </div>
  );
};

//  orphan heading prevention
export const TemplateElevenResumeSectionWithSubsections = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  const childrenArray = React.Children.toArray(children);
  const firstChild = childrenArray[0];
  const restChildren = childrenArray.slice(1);
  
  return (
    <div onClick={handleClickCallback} style={{ cursor: "pointer" }}>
      {/* Wrap heading with first child to prevent orphan headings */}
      {heading && firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["0.5"],
            marginTop: spacing["4"],
          }}
          wrap={false}
        >
          {/* Heading */}
          <View
            style={{
              ...styles.flexCol,
              width: "100%",
              textAlign: "left",
            }}
        >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </View>
          {/* First child */}
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["1"],
              paddingLeft: spacing["2"],
              marginTop: spacing["0.5"], 
            }}
          >
            {firstChild}
          </View>
        </View>
      )}
      
      {/* Heading only (when no children) */}
      {heading && !firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["0.5"],
            marginTop: spacing["3"],
          }}
        >
          <View
            style={{
              ...styles.flexCol,
              width: "100%",
              textAlign: "left",
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {capitalize(heading)}
            </Text>
          </View>
        </View>
      )}
      
      {/* Rest of children */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"],
            paddingLeft: spacing["2"],
            marginTop: spacing["0.5"],
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};

export const TemplateElevenPDFText = ({
  bold = false,
  themeColor,
  style = {},
  children,
}: {
  bold?: boolean;
  themeColor?: string;
  style?: Style;
  children: React.ReactNode;
}) => (
    <Text
    style={{
      color: themeColor || DEFAULT_FONT_COLOR,
      fontWeight: bold ? "bold" : "normal",
      lineHeight: "1.3",
      ...style,
    }}
    debug={DEBUG_RESUME_PDF_FLAG}
  >
    {children}
  </Text>
);
